import { useMemo, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import DataTable from "@/components/ui/dataTable";
import type { ClienteFinal } from "@interfaces/user.interface";
import { getClienteFinalColumns } from "@/lib/columns/clienteFinal.columns";
import { RegisterClienteFinalWithUsuarioForm } from "@/components/forms/cliente-final/RegisterClienteFinalWithUsuario";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  getClientesFinalesScopeConfig,
  type ClientesFinalesScope,
} from "../config/clientesFinalesScopeConfig";

interface ClientesFinalesPageProps {
  scope?: ClientesFinalesScope;
}

export function ClientesFinalesPage({
  scope = "all",
}: ClientesFinalesPageProps) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");

  const scopeConfig = getClientesFinalesScopeConfig(scope);
  const { data, isLoading, isFetching } = scopeConfig.useClientes(
    page,
    limit,
    search,
  );

  const clienteFinalColumns = useMemo(
    () =>
      getClienteFinalColumns(
        scopeConfig.buildColumnsKey(
          page,
          limit,
          search,
        ) as ReadonlyArray<unknown>,
      ),
    [scopeConfig, page, limit, search],
  );

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-6 w-80" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const createClienteFinalModal = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="headerAction" size="sm">
          {scopeConfig.buttonLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar Cliente Final</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          Llena el siguiente formulario para registrar un nuevo cliente final.
        </DialogDescription>
        <div className="py-4">
          <RegisterClienteFinalWithUsuarioForm
            setOpen={setOpen}
            queryKey={[...scopeConfig.queryInvalidateKey] as string[]}
          />
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <section className="w-full p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
      <div>
        <DataTable<ClienteFinal>
          columns={clienteFinalColumns}
          data={data?.data ?? []}
          total={data?.total ?? 0}
          page={page}
          limit={limit}
          onPageChange={setPage}
          isLoading={isFetching}
          search={search}
          onSearchChange={(value) => {
            setPage(1);
            setSearch(value);
          }}
          headerActions={createClienteFinalModal}
        />
      </div>
    </section>
  );
}
